import { useCallback, useContext } from "react";

// context
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";

// types
import { COUPON_GROUP_TYPES } from "@swiggy-private/cart-types";
import { CouponCardImpressionPayload } from "@swiggy-private/core-types";
import { CouponInfo } from "@swiggy-private/core-ui";
import { CouponPageType } from "@swiggy-private/checkout-core";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { CouponAnalytics } from "../types";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getRestaurantId } from "@cart-client/helpers/Cart";

interface CouponHandlersProps {
    croutonTitle: string;
    couponListingType: CouponPageType;
    isPaymentCouponListingPage: React.MutableRefObject<boolean>;
    cartResponse: ReviewedCart | null;
    paymentOffersRef: React.RefObject<HTMLDivElement>;
    setShowCrouton: (show: boolean) => void;
    analytics: CouponAnalytics;
}

export const useCouponHandlers = ({
    croutonTitle,
    couponListingType,
    isPaymentCouponListingPage,
    cartResponse,
    paymentOffersRef,
    setShowCrouton,
    analytics,
}: CouponHandlersProps) => {
    const { syncApplyCouponCart, handleCouponListingPageCouponRemove } = useContext(UpdateCartContext);
    const { setTncCoupon, clearErrorMessage, errorMessage } = useContext(CouponListingContext);
    const restaurantId = cartResponse ? getRestaurantId(cartResponse) : "";

    const applyCouponHandler = useCallback(
        ({ couponCode = "", offerId, isApplicable, type, timerPayload, couponType }: CouponCardImpressionPayload) => {
            if (offerId && isApplicable) {
                analytics.handleClickCouponOffers({
                    couponCode,
                    couponType: couponType || "",
                    offerId,
                    isApplicable,
                    timerPayload,
                    storeId: restaurantId,
                });
            } else {
                analytics.handleClickApplyCoupon(couponCode);
            }

            if (type === COUPON_GROUP_TYPES.APPLIED_OFFERS) {
                analytics.handleClickRemoveCoupon(couponCode, couponType || "");
                handleCouponListingPageCouponRemove(couponListingType, couponCode);
            } else {
                syncApplyCouponCart({
                    couponCode,
                    isCouponListingFlow: true,
                    isPaymentCouponListingFlow: isPaymentCouponListingPage.current,
                });
            }
        },
        [
            analytics,
            restaurantId,
            handleCouponListingPageCouponRemove,
            couponListingType,
            syncApplyCouponCart,
            isPaymentCouponListingPage,
        ],
    );

    const closeTncCouponHalfcard = useCallback(() => {
        setTncCoupon?.(null);
    }, [setTncCoupon]);

    const closeBottomSheet = useCallback(() => {
        if (!isEmpty(errorMessage)) {
            analytics.handleClickHalfCardInvalidCoupon(errorMessage);
        }
        clearErrorMessage();
    }, [clearErrorMessage, errorMessage, analytics]);

    const showTncCouponHalfcard = useCallback(
        (coupon: CouponInfo) => {
            setTncCoupon?.(coupon);
        },
        [setTncCoupon],
    );

    const onViewEnter = useCallback(() => {
        setShowCrouton(false);
    }, [setShowCrouton]);

    const onCroutonOfferClick = useCallback(() => {
        analytics.handleClickOfferCrouton(croutonTitle, cartResponse);
        paymentOffersRef.current?.scrollIntoView({ behavior: "smooth" });
        setShowCrouton(false);
    }, [analytics, croutonTitle, cartResponse, paymentOffersRef, setShowCrouton]);

    return {
        applyCouponHandler,
        closeTncCouponHalfcard,
        closeBottomSheet,
        showTncCouponHalfcard,
        onViewEnter,
        onCroutonOfferClick,
    };
};
